import { useParams } from '@tanstack/react-router'
import { Button } from '@wedding-tech/ui/button'
import { Card, CardContent } from '@wedding-tech/ui/card'
import confetti from 'canvas-confetti'
import { Heart, Maximize, Minimize, Sparkles } from 'lucide-react'
import {
  animate,
  AnimatePresence,
  domAnimation,
  LazyMotion,
  m,
  useMotionValue,
} from 'motion/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useDisplayToken } from '@/routes/e/$eventId/_display'

import { type BubbleState, useWallDisplayData } from './useWallDisplayData'

type PhysicsState = {
  id: string
  x: number
  y: number
  vx: number
  vy: number
  width: number
  height: number
}

const WheelDisplay = ({
  items,
  isSpinning,
  winnerIndex,
  duration,
}: {
  items: BubbleState[]
  isSpinning: boolean
  winnerIndex: number | null
  duration: number
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rotation = useMotionValue(0)

  useEffect(() => {
    if (isSpinning) {
      rotation.set(0)
      const controls = animate(rotation, 360 * 10, {
        duration: 1.5,
        ease: 'linear',
        repeat: Infinity,
      })
      return () => controls.stop()
    } else if (winnerIndex !== null) {
      const sliceAngle = 360 / items.length
      const itemCenterAngle = winnerIndex * sliceAngle + sliceAngle / 2
      const current = rotation.get()
      const rounds = Math.ceil(current / 360) + 3
      const targetRotation = rounds * 360 - 90 - itemCenterAngle
      animate(rotation, targetRotation, {
        duration: duration / 1000,
        ease: [0.45, 0.05, 0.55, 0.95],
      })
    }
  }, [isSpinning, winnerIndex, items.length, duration])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    let animationFrame: number

    const draw = () => {
      const { width, height } = canvas
      ctx.clearRect(0, 0, width, height)
      const centerX = width / 2
      const centerY = height / 2
      const radius = Math.min(width, height) / 2 - 40
      ctx.save()
      ctx.translate(centerX, centerY)
      ctx.rotate((rotation.get() * Math.PI) / 180)
      const sliceAngle = (Math.PI * 2) / items.length
      items.forEach((item, i) => {
        const startAngle = i * sliceAngle
        const endAngle = (i + 1) * sliceAngle
        ctx.beginPath()
        ctx.moveTo(0, 0)
        ctx.arc(0, 0, radius, startAngle, endAngle)
        ctx.closePath()
        ctx.fillStyle = i % 2 === 0 ? '#fff1f2' : '#ffe4e6'
        ctx.fill()
        ctx.strokeStyle = 'rgba(244,63,94,0.1)'
        ctx.lineWidth = 2
        ctx.stroke()
        ctx.save()
        ctx.rotate(startAngle + sliceAngle / 2)
        ctx.textAlign = 'right'
        ctx.fillStyle = '#881337'
        const fontSize = Math.max(8, Math.min(20, (radius * 2) / items.length))
        ctx.font = `bold ${fontSize}px sans-serif`
        ctx.fillText(item.name.slice(0, 15), radius - 15, 0)
        ctx.restore()
      })
      ctx.restore()
      ctx.beginPath()
      ctx.arc(centerX, centerY, radius + 5, 0, Math.PI * 2)
      ctx.strokeStyle = '#fb7185'
      ctx.lineWidth = 8
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(centerX, centerY, 20, 0, Math.PI * 2)
      ctx.fillStyle = '#fb7185'
      ctx.fill()
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 4
      ctx.stroke()
      ctx.save()
      ctx.translate(centerX, centerY - radius - 20)
      ctx.beginPath()
      ctx.moveTo(0, 0)
      ctx.lineTo(-20, -30)
      ctx.lineTo(20, -30)
      ctx.closePath()
      ctx.fillStyle = '#fb7185'
      ctx.fill()
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 2
      ctx.stroke()
      ctx.restore()
      animationFrame = requestAnimationFrame(draw)
    }

    animationFrame = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(animationFrame)
  }, [items, rotation])

  return (
    <canvas
      ref={canvasRef}
      width={800}
      height={800}
      className="max-h-[70vh] max-w-full drop-shadow-[0_0_50px_rgba(250,204,21,0.2)]"
    />
  )
}

const SlotDisplay = ({
  items,
  isSpinning,
  winnerIndex,
  duration,
}: {
  items: BubbleState[]
  isSpinning: boolean
  winnerIndex: number | null
  duration: number
}) => {
  return (
    <div className="relative h-100 w-full max-w-md overflow-hidden rounded-3xl border border-rose-100 bg-white shadow-2xl">
      <div className="absolute inset-x-0 top-0 z-10 h-32 bg-linear-to-b from-white to-transparent" />
      <div className="absolute inset-x-0 bottom-0 z-10 h-32 bg-linear-to-t from-white to-transparent" />
      <div className="absolute top-1/2 left-0 z-20 h-24 w-full -translate-y-1/2 border-y-2 border-rose-500/30 bg-rose-500/5" />
      <m.div
        animate={
          winnerIndex !== null
            ? { y: -(winnerIndex * 100) + 150 }
            : isSpinning
              ? { y: [0, -items.length * 50] }
              : { y: 0 }
        }
        transition={
          isSpinning
            ? { repeat: Infinity, duration: 0.5, ease: 'linear' }
            : { duration: duration / 1000, ease: [0.45, 0.05, 0.55, 0.95] }
        }
        className="flex flex-col items-center"
      >
        {items.map((item, i) => (
          <div
            key={`${item.id}-${i}`}
            className="flex h-25 w-full items-center gap-4 px-8"
          >
            <Avatar className="size-16 border-2 border-rose-100">
              <AvatarImage src={item.pictureUrl} />
              <AvatarFallback className="bg-rose-50 text-rose-500">
                {item.name.slice(0, 2)}
              </AvatarFallback>
            </Avatar>
            <span className="text-2xl font-bold text-rose-900">
              {item.name}
            </span>
          </div>
        ))}
      </m.div>
    </div>
  )
}

export const WallDisplayPage = () => {
  // eventId in the URL is the API's weddingId — the frontend route param was
  // renamed, the Go API's path param was not.
  const { eventId } = useParams({ from: '/e/$eventId/_display/wall' })
  const token = useDisplayToken()
  const { wallState, allCheckins, isUnauthorized } = useWallDisplayData(
    eventId,
    token,
  )

  const [bubbles, setBubbles] = useState<BubbleState[]>([])
  const [highlightedId, setHighlightedId] = useState<string | null>(null)
  // Escape / the Close button hide the overlay on THIS screen only. The
  // display group is read-only by construction (docs/adr/0002), and the
  // server decides when a winner actually expires (docs/adr/0003), so a
  // projector cannot clear it for everyone — the backoffice does that.
  const [dismissedWinnerId, setDismissedWinnerId] = useState<string | null>(
    null,
  )
  const [slotItems, setSlotItems] = useState<BubbleState[]>([])
  const [slotWinnerIndex, setSlotWinnerIndex] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imageCache = useRef<Map<string, HTMLImageElement>>(new Map())
  const physicsDataRef = useRef<Map<string, PhysicsState>>(new Map())
  const containerSizeRef = useRef({ width: 0, height: 0 })
  const highlightedIdRef = useRef<string | null>(null)
  const winnerIdRef = useRef<string | null>(null)
  const adaptiveScaleRef = useRef(1)
  const prevWinnerIdRef = useRef<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isFullscreenSupported, setIsFullscreenSupported] = useState(false)

  const isSpinning = wallState.isSpinning
  const settings = wallState.settings
  // A winner dismissed on this screen stays hidden here until the next one
  // arrives; the state itself is untouched.
  const winner =
    wallState.winner && wallState.winner.id !== dismissedWinnerId
      ? wallState.winner
      : null
  const currentSessionId = wallState.currentSessionId
  const sessions = wallState.sessions
  const activeSession = sessions.find((s) => s.id === currentSessionId)
  const sessionName = activeSession ? activeSession.name : 'เซสชันที่ 1'

  useEffect(() => {
    setIsFullscreenSupported(
      !!(
        document.fullscreenEnabled ||
        (document as any).webkitFullscreenEnabled ||
        (document as any).mozFullScreenEnabled
      ),
    )
  }, [])

  useEffect(() => {
    highlightedIdRef.current = highlightedId
  }, [highlightedId])

  useEffect(() => {
    winnerIdRef.current = winner?.id ?? null
  }, [winner])

  // The keydown listener below is registered once, so it can't read `winner`
  // from its closure — by the time anyone presses Escape that value is many
  // renders stale (and is usually null, since a display is normally opened
  // before a winner exists). It reads this ref instead. Deliberately tracks
  // the *server's* winner, not the locally-filtered one, so the handler knows
  // what to dismiss even after a previous dismissal.
  const serverWinnerIdRef = useRef<string | null>(null)
  useEffect(() => {
    serverWinnerIdRef.current = wallState.winner?.id ?? null
  }, [wallState.winner])

  // Bubble rotation — pause during spin
  useEffect(() => {
    const filter = settings.maritalStatusFilter ?? []
    const source =
      filter.length > 0
        ? allCheckins.filter((b) => {
            const m = b.maritalStatus || 'prefer_not_to_say'
            return filter.includes(m)
          })
        : allCheckins
    if (isSpinning || source.length === 0) return
    const MAX_VISIBLE = 50
    let currentIndex = 0
    const updateVisible = () => {
      if (source.length <= MAX_VISIBLE) {
        setBubbles(source)
        return
      }
      const next: BubbleState[] = []
      for (let i = 0; i < MAX_VISIBLE; i++) {
        next.push(source[(currentIndex + i) % source.length])
      }
      setBubbles(next)
      currentIndex = (currentIndex + MAX_VISIBLE) % source.length
    }
    updateVisible()
    const interval = setInterval(updateVisible, 15000)
    return () => clearInterval(interval)
  }, [allCheckins, isSpinning, settings.maritalStatusFilter])

  // Bubbles mode: flash random bubbles while spinning
  useEffect(() => {
    if (!isSpinning || settings.displayMethod !== 'bubbles') return
    const filter = settings.maritalStatusFilter ?? []
    const source =
      filter.length > 0
        ? allCheckins.filter((b) => {
            const m = b.maritalStatus || 'prefer_not_to_say'
            return filter.includes(m)
          })
        : allCheckins
    setBubbles(source)
    let cancelled = false
    const flash = () => {
      if (cancelled || !source.length) return
      highlightedIdRef.current =
        source[Math.floor(Math.random() * source.length)].id
      setTimeout(flash, 100)
    }
    flash()
    return () => {
      cancelled = true
      highlightedIdRef.current = null
    }
  }, [
    isSpinning,
    settings.displayMethod,
    settings.maritalStatusFilter,
    allCheckins,
  ])

  // Wheel/Slots: prepare slot items when spin starts with a pre-selected winner
  useEffect(() => {
    if (!isSpinning || settings.displayMethod === 'bubbles' || !winner) return
    const filter = settings.maritalStatusFilter ?? []
    const filteredCheckins =
      filter.length > 0
        ? allCheckins.filter((b) => {
            const m = b.maritalStatus || 'prefer_not_to_say'
            return filter.includes(m)
          })
        : allCheckins

    const shuffled = [...filteredCheckins].sort(() => Math.random() - 0.5)
    // The backoffice picks a winner by party (EventGuest) id, so bubbles match
    // on guestId, not on their own per-person id.
    if (!shuffled.find((b) => b.guestId === winner.id)) {
      // A party with no bubble is normal, not defensive: manual check-ins
      // never create an attendee row at all, so they can win without ever
      // having appeared on the wall.
      shuffled.push({
        id: winner.id,
        guestId: winner.id,
        name: winner.name,
        pictureUrl: winner.pictureUrl,
        initialX: 0,
        initialY: 0,
        vx: 0,
        vy: 0,
        initialScale: 1,
        scaleDuration: 5,
      })
    }
    setSlotItems(shuffled)
    setSlotWinnerIndex(shuffled.findIndex((b) => b.guestId === winner.id))
    setBubbles(filteredCheckins)
  }, [isSpinning]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handle winner arrival — confetti only. Auto-close is the server's call,
  // applied on every read (docs/adr/0003); useWallDisplayData schedules a
  // re-read at the deadline. Nothing here dismisses anything.
  useEffect(() => {
    if (!winner) {
      setHighlightedId(null)
      setSlotWinnerIndex(null)
      prevWinnerIdRef.current = null
      return
    }
    if (winner.id === prevWinnerIdRef.current) return
    prevWinnerIdRef.current = winner.id

    // The winner's own highlight rides on winnerIdRef, which is keyed by
    // guestId; highlightedIdRef is the spin-flash's per-person id and
    // the two must not be crossed.
    confetti({
      particleCount: 150,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#f43f5e', '#fb7185', '#fda4af', '#ffffff'],
    })
  }, [winner?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // A dismissal is scoped to one showing, not to a guest — so it clears as
  // soon as the winner goes away, not only when a different one arrives.
  // Without the null case, dismissing someone the admin later re-picks (via
  // reset-winners, or with excludeWinners off) would silently suppress their
  // second win forever.
  useEffect(() => {
    if (!wallState.winner || wallState.winner.id !== dismissedWinnerId) {
      setDismissedWinnerId(null)
    }
  }, [wallState.winner, dismissedWinnerId])

  const toggleFullscreen = useCallback(() => {
    const element = containerRef.current
    if (!element) return
    if (
      !document.fullscreenElement &&
      !(document as any).webkitFullscreenElement
    ) {
      const req =
        element.requestFullscreen ||
        (element as any).webkitRequestFullscreen ||
        (element as any).mozRequestFullScreen
      if (req) Promise.resolve(req.call(element)).catch(console.error)
    } else {
      const exit =
        document.exitFullscreen ||
        (document as any).webkitExitFullscreen ||
        (document as any).mozCancelFullScreen
      if (exit) exit.call(document)
    }
  }, [])

  useEffect(() => {
    const onFSChange = () => {
      setIsFullscreen(
        !!(
          document.fullscreenElement ||
          (document as any).webkitFullscreenElement
        ),
      )
    }
    const onKey = (e: KeyboardEvent) => {
      if (
        e.key.toLowerCase() === 'f' &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA'
      ) {
        toggleFullscreen()
      }
      if (e.key === 'Escape' && serverWinnerIdRef.current) {
        setDismissedWinnerId(serverWinnerIdRef.current)
      }
    }
    document.addEventListener('fullscreenchange', onFSChange)
    document.addEventListener('webkitfullscreenchange', onFSChange)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('fullscreenchange', onFSChange)
      document.removeEventListener('webkitfullscreenchange', onFSChange)
      window.removeEventListener('keydown', onKey)
    }
  }, [toggleFullscreen])

  const adaptiveScale =
    bubbles.length > 300
      ? 0.4
      : bubbles.length > 150
        ? 0.6
        : bubbles.length > 50
          ? 0.8
          : 1

  useEffect(() => {
    adaptiveScaleRef.current = adaptiveScale
  }, [adaptiveScale])

  // Canvas animation loop
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) return
    let lastTime = performance.now()
    let frameId: number

    const updateCanvasSize = () => {
      const container = containerRef.current
      if (!container) return
      const dpr = window.devicePixelRatio || 1
      const rect = container.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      canvas.style.width = `${rect.width}px`
      canvas.style.height = `${rect.height}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      containerSizeRef.current = { width: rect.width, height: rect.height }
    }

    updateCanvasSize()
    const resizeObserver = new ResizeObserver(updateCanvasSize)
    if (containerRef.current) resizeObserver.observe(containerRef.current)

    const drawBubble = (p: PhysicsState, b: BubbleState) => {
      const isH = highlightedIdRef.current === b.id
      const isW = !!b.guestId && winnerIdRef.current === b.guestId
      const scale = adaptiveScaleRef.current * (isW ? 1.5 : isH ? 1.2 : 1)
      const opacity = isW ? 1 : isH ? 1 : 0.8
      const x = (p.x / 100) * containerSizeRef.current.width
      const y = (p.y / 100) * containerSizeRef.current.height
      const size = 120 * scale
      const radius = size / 2

      ctx.save()
      ctx.translate(x + radius, y + radius)
      ctx.globalAlpha = opacity

      if (isW || isH) {
        ctx.shadowBlur = 40
        ctx.shadowColor = isW
          ? 'rgba(250, 204, 21, 0.6)'
          : 'rgba(255, 255, 255, 0.4)'
      }

      ctx.beginPath()
      ctx.arc(0, 0, radius, 0, Math.PI * 2)
      ctx.fillStyle = '#fff1f2'
      ctx.fill()

      const img = b.pictureUrl ? imageCache.current.get(b.pictureUrl) : null
      if (img && img.complete) {
        ctx.clip()
        ctx.drawImage(img, -radius, -radius, size, size)
      } else {
        ctx.fillStyle = '#f43f5e'
        ctx.font = `bold ${radius * 0.8}px sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(b.name.slice(0, 2).toUpperCase(), 0, 0)
      }

      ctx.restore()
      ctx.save()
      ctx.translate(x + radius, y + radius)
      ctx.beginPath()
      ctx.arc(0, 0, radius, 0, Math.PI * 2)
      ctx.strokeStyle = isW
        ? '#f43f5e'
        : isH
          ? '#fb7185'
          : 'rgba(244, 63, 94, 0.1)'
      ctx.lineWidth = 4
      ctx.stroke()

      const tagWidth = ctx.measureText(b.name).width + 30
      const tagHeight = 30 * scale
      ctx.translate(0, radius + 15)
      ctx.beginPath()
      ctx.roundRect(-tagWidth / 2, 0, tagWidth, tagHeight, 15)
      ctx.fillStyle = isW
        ? 'rgba(244, 63, 94, 0.1)'
        : 'rgba(255, 255, 255, 0.8)'
      ctx.fill()
      ctx.strokeStyle = 'rgba(244, 63, 94, 0.1)'
      ctx.stroke()
      ctx.fillStyle = isW || isH ? '#881337' : '#9f1239'
      ctx.font = `bold ${14 * scale}px sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(b.name, 0, tagHeight / 2)
      ctx.restore()
    }

    const update = (time: number) => {
      const delta = Math.min(time - lastTime, 32)
      lastTime = time
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      const { width: pW, height: pH } = containerSizeRef.current
      const baseBubbleWidth = 120 * adaptiveScaleRef.current
      const baseBubbleHeight = 160 * adaptiveScaleRef.current

      bubbles.forEach((b) => {
        const p = physicsDataRef.current.get(b.id)
        if (!p) return
        p.x += p.vx * delta
        p.y += p.vy * delta
        const xPx = (p.x / 100) * pW
        const yPx = (p.y / 100) * pH
        if (xPx < 0) {
          p.x = 0
          p.vx = Math.abs(p.vx)
        } else if (xPx + baseBubbleWidth > pW) {
          p.x = ((pW - baseBubbleWidth) / pW) * 100
          p.vx = -Math.abs(p.vx)
        }
        if (yPx < 0) {
          p.y = 0
          p.vy = Math.abs(p.vy)
        } else if (yPx + baseBubbleHeight > pH) {
          p.y = ((pH - baseBubbleHeight) / pH) * 100
          p.vy = -Math.abs(p.vy)
        }
        drawBubble(p, b)
      })

      frameId = requestAnimationFrame(update)
    }

    frameId = requestAnimationFrame(update)
    return () => {
      cancelAnimationFrame(frameId)
      resizeObserver.disconnect()
    }
  }, [bubbles])

  // Sync physics data and preload images
  useEffect(() => {
    const currentIds = new Set(bubbles.map((b) => b.id))
    physicsDataRef.current.forEach((_, id) => {
      if (!currentIds.has(id)) physicsDataRef.current.delete(id)
    })
    bubbles.forEach((b) => {
      if (!physicsDataRef.current.has(b.id)) {
        physicsDataRef.current.set(b.id, {
          id: b.id,
          x: b.initialX,
          y: b.initialY,
          vx: b.vx,
          vy: b.vy,
          width: 120,
          height: 160,
        })
      }
      if (b.pictureUrl && !imageCache.current.has(b.pictureUrl)) {
        const img = new Image()
        img.src = b.pictureUrl
        img.onload = () => imageCache.current.set(b.pictureUrl!, img)
      }
    })
  }, [bubbles])

  // An absent, expired, revoked or wrong-wedding token all surface as 404 —
  // the API refuses to say which, so neither can this screen. What the person
  // standing at the projector needs is the same either way: a fresh link.
  if (isUnauthorized) {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center gap-4 bg-[#fff5f7] px-8 text-center">
        <Heart className="size-12 text-rose-300" />
        <h1 className="text-2xl font-bold text-rose-900">
          จอนี้ยังไม่ได้เชื่อมต่อ
        </h1>
        <p className="max-w-md text-rose-900/60">
          เปิดหน้านี้อีกครั้งด้วยลิงก์สำหรับจอแสดงผลจากระบบหลังบ้าน
          หากลิงก์หมดอายุแล้ว ให้สร้างใหม่ในเมนูจัดการจอแสดงผล
        </p>
      </div>
    )
  }

  return (
    <LazyMotion features={domAnimation}>
      <div
        ref={containerRef}
        className="relative flex h-screen w-screen overflow-hidden bg-[#fff5f7] selection:bg-none"
      >
        {/* Dynamic Background Image */}
        {settings.backgroundImageUrl ? (
          <div
            className="animate-fade-in absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000 ease-in-out"
            style={{ backgroundImage: `url(${settings.backgroundImageUrl})` }}
          />
        ) : (
          <>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50vw_50vw,rgba(255,182,193,0.4),transparent)]" />
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/pinstripe.png')] opacity-10" />
          </>
        )}

        <canvas
          ref={canvasRef}
          className="absolute inset-0"
        />

        {/* Wheel/Slots overlay */}
        <AnimatePresence>
          {isSpinning &&
            (settings.displayMethod === 'wheel' ||
              settings.displayMethod === 'slots') && (
              <m.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-xl"
              >
                <div className="flex flex-col items-center gap-8">
                  <h2 className="text-3xl font-bold tracking-[0.2em] text-white uppercase drop-shadow-2xl">
                    Picking a winner…
                  </h2>
                  {settings.displayMethod === 'wheel' ? (
                    <WheelDisplay
                      items={slotItems}
                      isSpinning={isSpinning}
                      winnerIndex={slotWinnerIndex}
                      duration={settings.spinDuration * 1000}
                    />
                  ) : (
                    <SlotDisplay
                      items={slotItems}
                      isSpinning={isSpinning}
                      winnerIndex={slotWinnerIndex}
                      duration={settings.spinDuration * 1000}
                    />
                  )}
                </div>
              </m.div>
            )}
        </AnimatePresence>

        {/* Winner modal */}
        <AnimatePresence>
          {winner && !isSpinning && (
            <m.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-md"
            >
              <m.div
                initial={{ y: 50 }}
                animate={{ y: 0 }}
                className="relative w-full max-w-lg p-8 text-center"
              >
                <div className="absolute top-1/2 left-1/2 -z-10 size-125 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full bg-rose-500/20 blur-3xl" />
                <m.div
                  animate={{ rotate: [0, -10, 10, -10, 10, 0] }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="mb-8 flex justify-center"
                >
                  <div className="relative">
                    <Heart className="size-24 fill-rose-500 text-rose-500 drop-shadow-[0_0_20px_rgba(244,63,94,0.3)]" />
                    <Sparkles className="absolute -top-6 -left-6 size-12 animate-pulse text-rose-400" />
                    <Sparkles className="absolute -right-6 -bottom-6 size-12 animate-pulse text-rose-400 delay-700" />
                    <m.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                      className="absolute -top-2 -right-2 flex size-10 items-center justify-center rounded-full bg-white text-rose-500 shadow-md"
                    >
                      <span className="text-xl font-bold">💍</span>
                    </m.div>
                  </div>
                </m.div>
                <h2 className="mb-2 text-2xl font-bold tracking-widest text-rose-600 uppercase">
                  Congratulations
                </h2>
                <Card className="overflow-hidden border-rose-100 bg-white">
                  <CardContent className="p-10">
                    <div className="mb-6 flex justify-center">
                      <Avatar className="size-48 border-4 border-rose-500 p-1 shadow-[0_0_40px_rgba(244,63,94,0.2)]">
                        <AvatarImage
                          src={winner.pictureUrl}
                          className="rounded-full object-cover"
                        />
                        <AvatarFallback className="bg-rose-50 text-4xl text-rose-500 uppercase">
                          {winner.name.slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    <h3 className="text-4xl font-bold text-rose-900">
                      {winner.name}
                    </h3>
                    <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-rose-500/20 bg-rose-500/10 px-4 py-1.5 text-sm font-bold tracking-wide text-rose-600">
                      🎉 {sessionName}
                    </div>
                  </CardContent>
                </Card>
                <Button
                  variant="ghost"
                  onClick={() => setDismissedWinnerId(winner.id)}
                  className="mt-8 rounded-full text-rose-400 hover:bg-rose-50 hover:text-rose-600"
                >
                  Close (Press ESC)
                </Button>
              </m.div>
            </m.div>
          )}
        </AnimatePresence>

        {/* Footer — guest count + fullscreen */}
        <div className="absolute bottom-10 left-0 z-10 flex w-full items-center justify-between px-10">
          <div className="pointer-events-none hidden md:block" />
          {/* <div className="pointer-events-auto inline-flex items-center gap-3 rounded-full border border-rose-200 bg-white/80 px-6 py-3 shadow-lg backdrop-blur-xl">
          <div className="relative flex size-2 items-center justify-center">
            <div className="absolute size-full animate-ping rounded-full bg-rose-500 opacity-75"></div>
            <div className="relative size-2 rounded-full bg-rose-500"></div>
          </div>
          <span className="text-sm font-semibold tracking-wide text-rose-900/70">
            {bubbles.length} GUESTS CHECKED-IN
          </span>
        </div> */}
          {isFullscreenSupported && (
            <Button
              size="lg"
              onClick={toggleFullscreen}
              className="pointer-events-auto size-12 rounded-full border border-rose-200 bg-white/80 p-0 shadow-lg backdrop-blur-xl hover:bg-rose-50"
              title="Toggle Fullscreen (F)"
            >
              {isFullscreen ? (
                <Minimize className="size-6 text-rose-600" />
              ) : (
                <Maximize className="size-6 text-rose-600" />
              )}
            </Button>
          )}
        </div>
      </div>
    </LazyMotion>
  )
}
